import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import SpinnerLoader from "../../../components/common/SpinnerLoader"
import AgreementFilters from "../../../components/admin/legal-documents/AgreementFilters"
import AgreementTable from "../../../components/admin/legal-documents/AgreementTable"
import Header from "../../../components/admin/common/Header"

const AgreementManagement = () => {
  const [agreements, setAgreements] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    page: 1,
    limit: 20,
  })
  const [pagination, setPagination] = useState({})
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    superseded: 0,
    revoked: 0,
  })

  const authInfo = useSelector((state) => state.auth.authInfo)

  useEffect(() => {
    fetchAgreements()
  }, [filters])

  const fetchAgreements = async () => {
    try {
      setLoading(true)
      const queryParams = new URLSearchParams()

      // Add filters to query params
      if (filters.status) queryParams.append("status", filters.status)
      if (filters.page) queryParams.append("page", filters.page)
      if (filters.limit) queryParams.append("limit", filters.limit)

      // Use the actual backend endpoint
      const response = await axios.get(`admin/legaldocument/agreements?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        setAgreements(response.data.data.agreements)
        setPagination({
          totalPages: response.data.data.totalPages,
          currentPage: response.data.data.currentPage,
          hasNextPage: response.data.data.currentPage < response.data.data.totalPages,
          hasPrevPage: response.data.data.currentPage > 1,
          totalAgreements: response.data.data.totalAgreements,
        })

        // Calculate stats from the agreements data
        calculateStats(response.data.data.agreements)
      }
    } catch (error) {
      console.error("Error fetching agreements:", error)
      toast.error("Failed to fetch agreements")
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (agreementsData) => {
    const stats = {
      total: agreementsData.length,
      active: 0,
      superseded: 0,
      revoked: 0,
    }

    agreementsData.forEach((agreement) => {
      if (agreement.status === "active") stats.active++
      else if (agreement.status === "superseded") stats.superseded++
      else if (agreement.status === "revoked") stats.revoked++
    })

    setStats(stats)
  }

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1,
    }))
  }

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleViewUserHistory = async (userId) => {
    try {
      const response = await axios.get(`admin/legaldocument/agreements/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        // You can open a modal or navigate to a detailed view
        console.log("User agreement history:", response.data.data)
        toast.success("User agreement history loaded")
      }
    } catch (error) {
      console.error("Error fetching user history:", error)
      toast.error("Failed to fetch user agreement history")
    }
  }

  const handleCheckCompliance = async (userId) => {
    try {
      const response = await axios.get(`admin/legaldocument/compliance/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        const compliance = response.data.data
        if (compliance.isCompliant) {
          toast.success("User is fully compliant")
        } else {
          toast.warning(`User has ${compliance.missingAgreements.length} missing agreements`)
        }
      }
    } catch (error) {
      console.error("Error checking compliance:", error)
      toast.error("Failed to check user compliance")
    }
  }

  return (
    <>
    <Header />
    <div 
      className="agreement-management" 
      style={{ 
        minHeight: "100vh", 
        overflowY: "auto", 
        padding: '18px 100px' 
      }}
    >
      <style jsx>{`
        @media (max-width: 1200px) {
          .agreement-management {
            padding: 18px 50px !important;
          }
        }
        
        @media (max-width: 992px) {
          .agreement-management {
            padding: 18px 30px !important;
          }
        }
        
        @media (max-width: 768px) {
          .agreement-management {
            padding: 15px 20px !important;
          }
          
          .page-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 15px;
          }
          
          .page-header .btn {
            width: 100%;
            justify-content: center;
          }
          
          .page-header h2 {
            font-size: 1.5rem;
          }
          
          .page-header p {
            font-size: 0.875rem;
          }
          
          .stats-card .card-body {
            padding: 1rem 0.75rem;
          }
          
          .stats-card h4 {
            font-size: 1.5rem;
          }
          
          .stats-card p {
            font-size: 0.875rem;
          }
          
          .stats-card .fa-2x {
            font-size: 1.5em !important;
          }
          
          .table-container {
            max-height: 500px !important;
            overflow-x: auto;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table th,
          .table td {
            font-size: 0.875rem;
            padding: 0.5rem;
            white-space: nowrap;
          }
          
          .btn-sm {
            padding: 0.25rem 0.5rem;
            font-size: 0.8rem;
          }
          
          .empty-state h4 {
            font-size: 1.25rem;
          }
          
          .empty-state p {
            font-size: 0.875rem;
          }
          
          .empty-state .fa-3x {
            font-size: 2em !important;
          }
        }
        
        @media (max-width: 576px) {
          .agreement-management {
            padding: 12px 15px !important;
          }
          
          .page-header h2 {
            font-size: 1.25rem;
          }
          
          .page-header p {
            font-size: 0.8rem;
          }
          
          .stats-card .card-body {
            padding: 0.75rem;
          }
          
          .stats-card h4 {
            font-size: 1.25rem;
          }
          
          .stats-card p {
            font-size: 0.8rem;
          }
          
          .stats-card .fa-2x {
            font-size: 1.25em !important;
          }
          
          .table-container {
            max-height: 400px !important;
          }
          
          .table th,
          .table td {
            font-size: 0.8rem;
            padding: 0.375rem 0.25rem;
          }
          
          .btn-sm {
            padding: 0.2rem 0.4rem;
            font-size: 0.75rem;
            margin: 0.1rem;
          }
          
          .pagination {
            flex-wrap: wrap;
            justify-content: center;
          }
          
          .pagination .page-item {
            margin: 2px;
          }
          
          .pagination .page-link {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
          }
          
          /* Action buttons in table - make them stack */
          .action-buttons {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .action-buttons .btn {
            width: 100%;
            text-align: center;
          }
        }
        
        @media (max-width: 480px) {
          .agreement-management {
            padding: 10px 12px !important;
          }
          
          .stats-card .card-body {
            padding: 0.5rem;
          }
          
          .table-container {
            max-height: 350px !important;
            border-radius: 0.25rem;
          }
          
          .pagination .page-link {
            padding: 0.25rem 0.5rem;
            font-size: 0.8rem;
          }
          
          /* Hide some table columns on very small screens */
          .table .hide-on-mobile {
            display: none;
          }
          
          /* Make table more compact */
          .table th,
          .table td {
            padding: 0.25rem 0.125rem;
            font-size: 0.75rem;
          }
        }
        
        /* Custom scrollbar for table container */
        .table-container::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        
        .table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        /* Responsive table styling */
        @media (max-width: 768px) {
          .table-responsive-custom {
            display: block;
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .table-responsive-custom table {
            min-width: 600px;
          }
        }
        
        /* Stats cards responsive grid */
        @media (max-width: 768px) {
          .stats-row .col-md-3 {
            margin-bottom: 1rem;
          }
          
          .stats-row .col-md-3:last-child {
            margin-bottom: 0;
          }
        }
        
        /* Filter section responsive */
        .filter-section {
          margin-bottom: 1.5rem;
        }
        
        @media (max-width: 768px) {
          .filter-section {
            margin-bottom: 1rem;
          }
        }
      `}</style>
      
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 page-header">
              <div>
                <h2 className="mb-1">Agreement Management</h2>
                <p className="text-muted">Monitor and manage user legal agreements</p>
              </div>
              <Link to="/admin/legal-documents" className="btn btn-outline-primary">
                <i className="fas fa-file-alt me-2"></i>
                Manage Documents
              </Link>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4 stats-row">
              <div className="col-md-3 col-sm-6 mb-3 mb-md-0">
                <div className="card bg-primary text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.total}</h4>
                        <p className="mb-0">Total Agreements</p>
                      </div>
                      <i className="fas fa-handshake fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 mb-3 mb-md-0">
                <div className="card bg-success text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.active}</h4>
                        <p className="mb-0">Active Agreements</p>
                      </div>
                      <i className="fas fa-check-circle fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 mb-3 mb-sm-0">
                <div className="card bg-warning text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.superseded}</h4>
                        <p className="mb-0">Superseded</p>
                      </div>
                      <i className="fas fa-history fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6">
                <div className="card bg-danger text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.revoked}</h4>
                        <p className="mb-0">Revoked</p>
                      </div>
                      <i className="fas fa-ban fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="filter-section">
              <AgreementFilters filters={filters} onFilterChange={handleFilterChange} />
            </div>

            {/* Agreements Table */}
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">User Agreements</h5>
              </div>
              <div 
                className="card-body p-0 table-container table-responsive-custom" 
                style={{ maxHeight: "600px", overflowY: "auto" }}
              >
                {loading ? (
                  <div className="text-center py-5">
                    <SpinnerLoader />
                  </div>
                ) : agreements.length === 0 ? (
                  <div className="text-center py-5 empty-state">
                    <i className="fas fa-handshake fa-3x text-muted mb-3"></i>
                    <h4>No Agreements Found</h4>
                    <p className="text-muted">No user agreements match your current filters</p>
                  </div>
                ) : (
                  <AgreementTable
                    agreements={agreements}
                    onViewUserHistory={handleViewUserHistory}
                    onCheckCompliance={handleCheckCompliance}
                  />
                )}
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <nav aria-label="Agreements pagination" className="mt-4">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${!pagination.hasPrevPage ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                    >
                      Previous
                    </button>
                  </li>

                  {[...Array(pagination.totalPages)].map((_, index) => (
                    <li key={index + 1} className={`page-item ${pagination.currentPage === index + 1 ? "active" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                        {index + 1}
                      </button>
                    </li>
                  ))}

                  <li className={`page-item ${!pagination.hasNextPage ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default AgreementManagement